import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import UserService from '../services/user'

function SetPassword() {
  const [searchParams] = useSearchParams()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [token, setToken] = useState('')
  const [tokenData, setTokenData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token')
    if (!tokenFromUrl) {
      setError('Invalid or missing token')
      setIsLoading(false)
      return
    }

    setToken(tokenFromUrl)

    // Validate token on page load
    UserService.validateToken(tokenFromUrl)
      .then((response) => {
        if (response.success) {
          setTokenData(response.data)
        } else {
          setError(response.error)
        }
      })
      .catch(() => {
        setError('Failed to validate token. Please try again.')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [searchParams])

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value
    setPassword(newPassword)
    setPasswordStrength(UserService.getPasswordStrength(newPassword))

    // Clear error when user starts typing
    if (error) {
      setError('')
    }
  }

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value)

    // Clear error when user starts typing
    if (error) {
      setError('')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!password || !confirmPassword) {
      setError('Please fill in both password fields')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    // Use comprehensive password validation
    const validation = UserService.validatePassword(password)
    if (!validation.isValid) {
      setError(validation.errors.join('. '))
      return
    }

    setIsSubmitting(true)

    try {
      const response = await UserService.setPassword(token, password)

      if (response.success) {
        setSuccess(true)
        setPassword('')
        setConfirmPassword('')
        setPasswordStrength(null)

        // Redirect to login after 3 seconds
        setTimeout(() => {
          window.location.href = '/login'
        }, 3000)
      } else {
        setError(response.error || 'Failed to set password')
      }
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="loading-content">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    )
  }

  if (!token || (!tokenData && !isLoading && error)) {
    return (
      <div className="login-page">
        <div className="login-container">
          <h1>Invalid Link</h1>
          <p className="subtitle">This invitation link is invalid or has expired</p>

          <div className="error show">
            {error || 'Invalid or missing token'}
          </div>

          <button
            onClick={() => window.location.href = '/login'}
            className="success-button"
            style={{ marginTop: '20px' }}
          >
            Go to Login
          </button>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="login-page">
        <div className="login-container">
          <h1>Success!</h1>
          <p className="subtitle">Your password has been set successfully</p>

          <div className="success-message">
            <div className="success-title">
              Password set successfully!
            </div>
            <div className="success-subtitle">
              Redirecting to login page in a few seconds...
            </div>
          </div>

          <button
            onClick={() => window.location.href = '/login'}
            className="success-button"
          >
            Go to Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <h1>Set Password</h1>
        <p className="subtitle">
          {tokenData ? `Welcome ${tokenData.full_name}! Create your password for ${tokenData.email}` : 'Create your new password'}
        </p>

        {error && (
          <div className={`error ${error ? 'show' : ''}`}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={handlePasswordChange}
              required
              autoComplete="new-password"
              placeholder="Enter new password"
              disabled={isSubmitting}
            />
            {passwordStrength && passwordStrength.level !== 'none' && (
              <div
                className="password-strength"
                data-strength={passwordStrength.level}
              >
                <div className="strength-bar">
                  <div className="strength-fill"></div>
                </div>
                <div className="strength-text">
                  {passwordStrength.text}
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              required
              autoComplete="new-password"
              placeholder="Confirm new password"
              disabled={isSubmitting}
            />
          </div>

          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Setting Password...' : 'Set Password'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default SetPassword